//Dealer Franchisee subclass views
import { createNewQuote } from '../quote-creator';
import { customElement, state } from 'lit/decorators.js';
import { defaultStagedThreshold, EventCancellation } from '../../../../webmodule-common/other/general/staged-event';
import { displaySupplierTACNotApprovedMsg } from '../../../v6config/supplier-services';
import { EventSnippet, NullPromise } from '../../../../webmodule-common/other/ui/events';
import { FranchiseeApi } from '../../../api/franchisee-api';
import { getApiFactory } from '../../../api/api-injector';
import { getQuoteContainer, getQuoteContainerManager } from '../quote-ui-adapter';
import { getSupplier } from '../supplier-selector';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { moneyToTemplateResult } from '../../../../webmodule-common/other/currency-formatter';
import { notificationSignal } from '../../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { QuoteContainer, QuoteContainerManager } from '../../../quotes/data/quote-container';
import { QuoteListView, QuoteSummaryTable, QuoteSummaryTableOptions } from '../../../quotes/views/quote-list-view';
import { QuoteState, ViewQuoteSummary, ViewQuoteTotalByState } from '../../../api/dealer-api-interface-quote';
import { SSEQuote } from '../../../quotes/data/sse-quote';
import { stagedExecution } from '../../../../webmodule-common/other/general/event-source-client';

import { userDataStore } from '../../common/current-user-data-store';
import { WMEventSource } from '../../../api/event-source';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { PageManager } from '../../../../webmodule-common/other/ui/page-control';
import { FranchiseeV6BranchQuoteSupplierDefaultsView } from './v6/franchisee-v6-branchdefaults';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import { FranchiseeQuoteSummaryTable } from './franchisee-quote-summary-table';

const numberOfDaysHistory = 30;

@customElement('wm-franchiseequotelistview')
export class FranchiseeQuoteListView extends QuoteListView {
  franchiseeApi: FranchiseeApi = getApiFactory().franchisee();
  @state()
  quoteTotals: ViewQuoteTotalByState[] | null = null;
  private staleQuotes: string[] = [];

  @state()
  quoteDefaultsTab = true;
  @state()
  quoteDefaultsUI?: FranchiseeV6BranchQuoteSupplierDefaultsView;
  async init() {
    await this.updatePageTotals();
    const table = this.pageControl.activePage?.data as QuoteSummaryTable;
    if (table) await table.refreshData(1);
  }

  async doAfterQuoteEdit() {
    await this.updatePageTotals();
  }

  private _eventCancel: EventCancellation = new EventCancellation();
  private _eventQuoteUpdated = stagedExecution({
    threshold: defaultStagedThreshold,
    cancelToken: this._eventCancel,
    eventFinally: () => notificationSignal(false),
    event: async () => {
      this.updatePageTotals();
      this.tables.forEach(t => (t.dataIsStale = true));
      const table = this.pageControl.activePage?.data as QuoteSummaryTable;
      this.staleQuotes = [];
      if (table) {
        await table.refreshData();
        table.dataIsStale = false;
      }
      //reload the page.. we dont know what moved, or where, or what state its in
    },
    eventTriggered: (_data: SSEQuote) => {
      notificationSignal(true);
    },
    eventActioned: async (data: SSEQuote) => {
      if (!this.staleQuotes.includes(data.id)) this.staleQuotes.push(data.id);
      const table = this.pageControl.activePage?.data as QuoteSummaryTable;
      if (table) {
        await table.refreshData();
        table.dataIsStale = false;
      }
    }
  });

  connectedCallback(): void {
    super.connectedCallback();
    WMEventSource.getInstance().addEventListener(WMEventSource.quote, this._eventQuoteUpdated);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    WMEventSource.getInstance().removeEventListener(WMEventSource.quote, this._eventQuoteUpdated);
  }

  protected createQuoteContainerManager(quoteContainer: QuoteContainer): QuoteContainerManager {
    return getQuoteContainerManager(quoteContainer);
  }

  protected async updatePageTotals() {
    const ownerId = await this.quoteOwnerId();
    const totals = await this.quoteApi.getQuoteTotalsByState({
      quoteOwnerId: ownerId,
      options: [
        {
          // eslint-disable-next-line no-bitwise
          state: QuoteState.Draft | QuoteState.Active,
          numberOfDaysHistory: null
        },
        {
          state: QuoteState.Draft,
          numberOfDaysHistory: null
        },
        {
          state: QuoteState.Active,
          numberOfDaysHistory: null
        },
        {
          // eslint-disable-next-line no-bitwise
          state: QuoteState.Issued | QuoteState.IssuePending,
          numberOfDaysHistory: null
        },
        {
          state: QuoteState.Accepted,
          numberOfDaysHistory: null
        },
        {
          // eslint-disable-next-line no-bitwise
          state: QuoteState.Accepted_AssignedToReviewer | QuoteState.SupplierReviewPending,
          numberOfDaysHistory: null
        },
        {
          state: QuoteState.SupplierReviewed,
          numberOfDaysHistory: null
        },
        {
          state:
            // eslint-disable-next-line no-bitwise
            QuoteState.Approved |
            QuoteState.Rejected |
            QuoteState.Accepted_RefusedBySupplier |
            QuoteState.Cancelled |
            QuoteState.Lapsed,
          numberOfDaysHistory: numberOfDaysHistory
        }
      ]
    });
    if (totals) this.quoteTotals = totals.totals.filter(x => x.quoteOwnerId === ownerId);
    this.pageControl.requestUpdate();
  }

  protected createQuoteContainer(quoteId: string): QuoteContainer {
    return getQuoteContainer(quoteId);
  }

  //this can be overridden in sub class to replace the table with different column/view etc
  protected quoteSummaryTableFactory(options: QuoteSummaryTableOptions): FranchiseeQuoteSummaryTable {
    return new FranchiseeQuoteSummaryTable(options);
  }

  beforeRefresh() {
    this.staleQuotes = [];
  }

  afterRefresh() {
    this._eventCancel.cancel();
  }

  async dispose(): Promise<void> {
    await super.dispose();
    this._eventCancel.cancel();
  }

  protected createSummaryTable(
    title: EventSnippet,
    quoteStates: QuoteState,
    pageFragment: string,
    numberOfDays?: number
  ): QuoteSummaryTable {
    return this.quoteSummaryTableFactory({
      quoteOwnerId: this.quoteOwnerId,
      quoteStates: quoteStates,
      numberOfDaysHistory: numberOfDays,
      customerCache: this.customerCache,
      userProfileCache: this.userProfileCache,
      title: title,
      copyQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.copyQuote(quoteSummary);
      },
      deleteQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.deleteQuote(quoteSummary);
      },
      pageFragment: pageFragment,
      stale: { exists: (id: string) => this.staleQuotes.includes(id) }
    });
  }

  //override this in a subclass to change the design, and order of the tables
  protected initializeTables(): QuoteSummaryTable[] {
    const title = (caption: string, state: QuoteState, hideTotal?: boolean) => {
      let total = 0;
      let count = 0;
      this.quoteTotals
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison,no-bitwise
        ?.filter(x => (state & x.quoteState) === x.quoteState)
        .forEach(x => {
          total += x.total;
          count += x.quoteCount;
        });
      return html`${caption} (${count})${hideTotal ? html`` : moneyToTemplateResult(total)}`;
    };
    const makeTable = (
      titleStr: () => string,
      state: QuoteState,
      pageFragment: string,
      numberOfDays?: number,
      hideTotal?: boolean
    ) => {
      return this.createSummaryTable(() => title(titleStr(), state, hideTotal), state, pageFragment, numberOfDays);
    };

    return [
      // eslint-disable-next-line no-bitwise
      makeTable(
        () => LangFranchiseeQuoteListView.allCurrentTableTitle(),
        QuoteState.Draft | QuoteState.Active,
        'allCurrent',
        undefined,
        true
      ),
      makeTable(() => LangFranchiseeQuoteListView.draftTableTitle(), QuoteState.Draft, 'draft', undefined, true),
      makeTable(() => LangFranchiseeQuoteListView.activeTableTitle(), QuoteState.Active, 'active', undefined, true),

      // eslint-disable-next-line no-bitwise
      makeTable(
        () => LangFranchiseeQuoteListView.issuedTableTitle(),
        QuoteState.Issued | QuoteState.IssuePending,
        'issued',
        undefined,
        true
      ),

      makeTable(
        () => LangFranchiseeQuoteListView.acceptedTableTitle(),
        QuoteState.Accepted,
        'accepted',
        undefined,
        true
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.underReviewTableTitle(),
        // eslint-disable-next-line no-bitwise
        QuoteState.Accepted_AssignedToReviewer | QuoteState.SupplierReviewPending,
        'underreview',
        undefined,
        true
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.reviewedTableTitle(),
        QuoteState.SupplierReviewed,
        'reviewed',
        undefined,
        true
      ),

      makeTable(
        () => LangFranchiseeQuoteListView.archivedTableTitle(),
        // eslint-disable-next-line no-bitwise
        QuoteState.Approved |
          QuoteState.Rejected |
          QuoteState.Accepted_RefusedBySupplier |
          QuoteState.Cancelled |
          QuoteState.Lapsed,
        'archived',
        numberOfDaysHistory,
        true
      )
    ];
  }

  protected async createNewQuote(): NullPromise<QuoteContainer> {
    const supplier = await getSupplier();
    if (isEmptyOrSpace(supplier.supplierId)) return null;
    if (!userDataStore.supplierTACApproved(supplier.supplierId)) {
      await displaySupplierTACNotApprovedMsg(supplier, true);
      return null;
    }
    const owner = await this.quoteOwnerId();

    return createNewQuote({ supplierId: supplier.supplierId, owner: owner });
  }

  protected getInitialPageManagers(): PageManager[] {
    const results = super.getInitialPageManagers();
    results.push(this.makeQuoteDefaultsPageManager());
    return results;
  }
  makeQuoteDefaultsPageManager(): PageManager {
    const pm: PageManager = {
      caption: () => LangFranchiseeQuoteListView.quoteDefaultsTab(),
      canClose: () => Promise.resolve(false),
      canLeave: async () => {
        return (await this.quoteDefaultsUI?.saveChanges()) ?? true;
      },
      hasDelete: () => false,
      onEnter: async () => {
        this.quoteDefaultsUI ??= await constructAsync(new FranchiseeV6BranchQuoteSupplierDefaultsView());

        //we need to load the defaults data from the server.
      },
      content: () => {
        return html`${this.quoteDefaultsUI}`;
      },
      data: undefined,
      pageFragment: 'quotedefaults',
      buttonMenu: () => this.quoteDefaultsUI?.buttonMenu() ?? ''
    };
    return pm;
  }
}
